import { useEffect } from 'react';

function isCustomEvent<SomeEvent extends CustomEvent>(
    event:Event,
    test?:((event:CustomEvent) => event is SomeEvent) | nullish,
): event is SomeEvent {
    return 'detail' in event && 'initCustomEvent' in event && (!test || test(event as CustomEvent));
}

const READY_OPTIMIZELY_LISTENER: Record<string, boolean> = {}
// Returns 0 if the listener was ready, otherwise 1 and we will retry in 100ms
const checkListener = (eventName: string, details: unknown ) => {
    if (READY_OPTIMIZELY_LISTENER[eventName]) {
        window.dispatchEvent(new CustomEvent(eventName, { detail: details }));
        return 0;
    }
    setTimeout(() => checkListener(eventName, details), 100);
    return 1;
}

window.OptimizelyEvents = window.OptimizelyEvents || [];
while (window.OptimizelyEvents.length) {
    const {eventName, details} = window.OptimizelyEvents.pop()!;
    checkListener(eventName, details);
}
window.OptimizelyEvents.push = ({ eventName, details }) => checkListener(eventName, details);

export function setUpOptimizelyListener<SomeEvent extends CustomEvent>(
    optimizelyEvent:string,
    callback:(event:SomeEvent) => unknown,
    test?:((event:CustomEvent) => event is SomeEvent) | nullish,
) {
    function eventListener(event: Event) {
        if (isCustomEvent<SomeEvent>(event, test)) {
            callback(event);
        }
    }
    window.addEventListener(optimizelyEvent, eventListener);
    READY_OPTIMIZELY_LISTENER[optimizelyEvent] = true;
    // Return event in case we need to remove the listener
    return eventListener;
}

export function useOptimizelyVariant<SomeEvent extends CustomEvent>(
        optimizelyEvent:string,
        callback:(event:SomeEvent) => unknown,
        test?:((event:CustomEvent) => event is SomeEvent) | nullish,
        deps:unknown[] = [],
) {
    useEffect(() => {
        const listenerFun = setUpOptimizelyListener<SomeEvent>(optimizelyEvent, callback, test)
        return () => {
            window.removeEventListener(optimizelyEvent, listenerFun);
            READY_OPTIMIZELY_LISTENER[optimizelyEvent] = false;
        }
    }, deps);
}
